import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, ChevronRight, Share2, Trash2, Globe, Sparkles, MoreHorizontal } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import client from "@/lib/api";
import { toast } from "sonner";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle,
} from "@/components/ui/drawer";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState(null);

  const load = () => {
    client.get("/projects").then(({ data }) => setProjects(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const share = async (p) => {
    const url = p.live_url || `${window.location.origin}`;
    try {
      if (navigator.share && p.live_url) {
        await navigator.share({ title: p.name, url });
      } else if (p.live_url) {
        await navigator.clipboard.writeText(url);
        toast.success("Live link copied!");
      } else {
        toast.info("Publish this site first to share it.");
      }
    } catch { /* cancelled */ }
    setSheet(null);
  };

  const remove = async (p) => {
    try {
      await client.delete(`/projects/${p.id}`);
      setProjects((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Project deleted");
    } catch {
      toast.error("Couldn't delete");
    }
    setSheet(null);
  };

  const firstName = (user?.name || "there").split(" ")[0];

  return (
    <AppShell
      title="Your builds"
      right={
        <button onClick={() => navigate("/app/templates")} data-testid="dashboard-new"
          className="tap-scale grid place-items-center w-10 h-10 rounded-full bg-ho-primary text-white">
          <Plus className="w-5 h-5" strokeWidth={2.5} />
        </button>
      }
    >
      <p className="text-ho-muted">Welcome back, <span className="font-semibold text-ho-ink">{firstName}</span>.</p>

      {loading ? (
        <div className="mt-8 space-y-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-20 animate-pulse rounded-3xl bg-black/5" />)}
        </div>
      ) : projects.length === 0 ? (
        <EmptyState onCreate={() => navigate("/app/templates")} />
      ) : (
        <div className="mt-5 space-y-3">
          {projects.map((p, i) => (
            <motion.div key={p.id}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="group flex items-center gap-3 rounded-3xl border border-black/5 bg-white p-3">
              <button onClick={() => navigate(`/app/build/${p.id}`)} data-testid={`project-${p.id}`}
                className="tap-scale flex flex-1 items-center gap-3 text-left">
                <div className="grid place-items-center w-12 h-12 shrink-0 rounded-2xl bg-ho-primary/10">
                  <Globe className="w-5 h-5 text-ho-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-display font-bold">{p.name}</div>
                  <div className="mt-0.5 flex items-center gap-2">
                    <StatusChip status={p.status} />
                    <span className="text-xs text-ho-muted">{timeAgo(p.updated_at)}</span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-black/20" />
              </button>
              <button onClick={() => setSheet(p)} data-testid={`project-menu-${p.id}`}
                className="tap-scale grid place-items-center w-9 h-9 rounded-full bg-black/5">
                <MoreHorizontal className="w-4 h-4 text-ho-muted" />
              </button>
            </motion.div>
          ))}
        </div>
      )}

      <Drawer open={!!sheet} onOpenChange={(o) => !o && setSheet(null)}>
        <DrawerContent data-testid="project-actions-sheet">
          <DrawerHeader>
            <DrawerTitle className="font-display">{sheet?.name}</DrawerTitle>
          </DrawerHeader>
          <div className="space-y-2 px-4 pb-8">
            <SheetAction icon={Sparkles} label="Open & edit" onClick={() => navigate(`/app/build/${sheet.id}`)} testid="sheet-edit" />
            <SheetAction icon={Share2} label={sheet?.live_url ? "Share live link" : "Publish to share"} onClick={() => share(sheet)} testid="sheet-share" />
            <SheetAction icon={Trash2} label="Delete project" danger onClick={() => remove(sheet)} testid="sheet-delete" />
          </div>
        </DrawerContent>
      </Drawer>
    </AppShell>
  );
}

const StatusChip = ({ status }) => {
  const live = status === "live";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${live ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${live ? "bg-emerald-500" : "bg-amber-500"}`} />
      {live ? "Live" : "Draft"}
    </span>
  );
};

const SheetAction = ({ icon: Icon, label, onClick, danger, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`tap-scale flex w-full items-center gap-3 rounded-2xl px-4 py-3.5 text-left font-semibold ${danger ? "bg-red-50 text-red-600" : "bg-black/5 text-ho-ink"}`}>
    <Icon className="w-5 h-5" /> {label}
  </button>
);

const EmptyState = ({ onCreate }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
    className="mt-10 rounded-[32px] border border-dashed border-black/15 bg-white p-8 text-center">
    <div className="mx-auto grid place-items-center w-16 h-16 rounded-3xl bg-ho-primary/10">
      <Sparkles className="w-8 h-8 text-ho-primary" />
    </div>
    <h3 className="font-display mt-5 text-xl font-bold">Build your first site</h3>
    <p className="mt-2 text-ho-muted">Pick a template or describe an idea. It takes seconds.</p>
    <button onClick={onCreate} data-testid="empty-create"
      className="tap-scale mt-6 inline-flex items-center gap-2 rounded-full bg-ho-primary px-6 py-3.5 font-semibold text-white">
      <Plus className="w-5 h-5" /> Create a site
    </button>
  </motion.div>
);

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
